import firebaseService from './FirebaseService';
import { AuthUser } from './AuthService';

export interface UserProfile {
  uid: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
  createdAt: string;
  lastLoginAt: string;
  updatedAt: string;
  preferences?: {
    theme?: 'light' | 'dark' | 'system';
    notifications?: boolean;
    language?: string;
  };
  stats?: {
    totalProjects?: number;
    totalPomodoros?: number;
    joinedAt?: string;
  };
}

class UserProfileService {
  /**
   * Create or update user profile when they sign in
   */
  static async syncUserProfile(authUser: AuthUser): Promise<UserProfile> {
    try {
      const now = new Date().toISOString();
      
      // Get existing profile or create default
      const existingProfile = await firebaseService.getUserProfile<UserProfile | null>(null);
      
      const profileData: UserProfile = {
        uid: authUser.uid,
        email: authUser.email || '',
        displayName: authUser.displayName,
        photoURL: authUser.photoURL,
        createdAt: existingProfile?.createdAt || now,
        lastLoginAt: now,
        updatedAt: now,
        preferences: existingProfile?.preferences || {
          theme: 'system',
          notifications: true,
          language: 'en'
        },
        stats: existingProfile?.stats || {
          totalProjects: 0,
          totalPomodoros: 0,
          joinedAt: now
        }
      };

      await firebaseService.setUserProfile(profileData);
      return profileData;
    } catch (error) {
      console.error('Error syncing user profile:', error);
      throw error;
    }
  }

  /**
   * Get user profile
   */
  static async getUserProfile(): Promise<UserProfile | null> {
    try {
      return await firebaseService.getUserProfile<UserProfile | null>(null);
    } catch (error) {
      console.error('Error getting user profile:', error);
      return null;
    }
  }

  /**
   * Update user preferences
   */
  static async updatePreferences(preferences: Partial<UserProfile['preferences']>): Promise<UserProfile> {
    try {
      const currentProfile = await this.getUserProfile();
      if (!currentProfile) {
        throw new Error('User profile not found');
      }

      const updates = {
        preferences: {
          ...currentProfile.preferences,
          ...preferences
        },
        updatedAt: new Date().toISOString()
      };

      return await firebaseService.updateUserProfile(updates);
    } catch (error) {
      console.error('Error updating user preferences:', error);
      throw error;
    }
  }

  /**
   * Update user stats (called when creating projects, completing pomodoros, etc.)
   */
  static async updateStats(statsUpdates: Partial<UserProfile['stats']>): Promise<UserProfile> {
    try {
      const currentProfile = await this.getUserProfile();
      if (!currentProfile) {
        throw new Error('User profile not found');
      }

      const updates = {
        stats: {
          ...currentProfile.stats,
          ...statsUpdates
        },
        updatedAt: new Date().toISOString()
      };

      return await firebaseService.updateUserProfile(updates);
    } catch (error) {
      console.error('Error updating user stats:', error);
      throw error;
    }
  }

  /**
   * Get user display information for UI
   */
  static async getUserDisplayInfo(): Promise<{
    displayName: string;
    email: string;
    photoURL: string | null;
    memberSince: string;
  } | null> {
    try {
      const profile = await this.getUserProfile();
      if (!profile) return null;

      return {
        displayName: profile.displayName || profile.email.split('@')[0] || 'User',
        email: profile.email,
        photoURL: profile.photoURL,
        memberSince: new Date(profile.createdAt).toLocaleDateString()
      };
    } catch (error) {
      console.error('Error getting user display info:', error);
      return null;
    }
  }
}

export default UserProfileService; 